import React from "react";
import { ActivityIndicator, StyleSheet, View, FlatList } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { colors } from "../../theme";
import Header from "../../components/Header";
import EmptyCard from "../../components/EmptyCard";
import VisitCard from "../../components/VisitCard";

import { useQuery } from "@apollo/client";
import { VISITS } from "../queries";
import { VisitFilterStatusType } from "../../types";
import { useFocusEffect } from "@react-navigation/native";
import Container from "../../components/Container";
import { Durations } from "../../styles";

const HistoryScreen = () => {
  const [loadingMore, setLoadingMore] = React.useState(false);
  const { data, loading, refetch, fetchMore } = useQuery(VISITS, {
    variables: {
      filters: { status: VisitFilterStatusType.Past },
      first: 20,
    },
    pollInterval: Durations.long,
  });

  const pageInfo = data?.visits?.pageInfo;
  const loadMore = async () => {
    if (loadingMore || !pageInfo?.hasNextPage || !pageInfo.endCursor) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          after: pageInfo.endCursor,
          first: 20,
        },
        updateQuery: (previous: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult) return previous;

          return {
            ...fetchMoreResult,
            visits: {
              ...fetchMoreResult.visits,
              edges: [
                ...(previous.visits?.edges || []),
                ...(fetchMoreResult.visits?.edges || []),
              ],
            },
          };
        },
      });
    } finally {
      setLoadingMore(false);
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch]),
  );

  return (
    <SafeAreaView style={styles.container} edges={["right", "top", "left"]}>
      <Container>
        <FlatList
          data={data?.visits?.edges || []}
          contentContainerStyle={styles.contentContainer}
          keyExtractor={(e) => e.node.id}
          renderItem={({ item }) => (
            <VisitCard key={item.key} visit={item.node} />
          )}
          onEndReached={loadMore}
          onEndReachedThreshold={0.4}
          ListFooterComponent={
            loadingMore ? (
              <ActivityIndicator
                color={colors.yellow}
                size="small"
                style={styles.loadingMore}
              />
            ) : null
          }
          stickyHeaderIndices={[0]}
          ListHeaderComponent={() => (
            <View style={styles.headerContainer}>
              <Header title="History" />
            </View>
          )}
          ListEmptyComponent={
            loading ? null : (
              <EmptyCard
                title="No History Available"
                description="Once you’ve completed a visit with a shop the details will appear here."
              />
            )
          }
        />
      </Container>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.black,
  },
  headerContainer: {
    backgroundColor: colors.black,
    marginBottom: 20,
  },
  contentContainer: {
    paddingHorizontal: 20,
  },
  loadingMore: {
    marginBottom: 24,
  },
});

export default HistoryScreen;
